import { useState } from 'react';
import { X, Loader2, UploadCloud, CheckCircle2, AlertTriangle } from 'lucide-react';
import { lohnBuchungsbelegApi, fileToBase64, fmtMoney, type ColumnMapping } from '../../services/lohnBuchungsbelegApi';
import type { PreviewResult } from '../../types/lohnBuchungsbeleg';

interface Props {
    defaultPeriod: string;
    onClose: () => void;
    onCreated: (runId: number) => void;
}

const REQUIRED_FIELDS: Array<[string, string]> = [
    ['soll', 'Fibu Soll'], ['haben', 'Fibu Haben'], ['lohnart', 'Lohnart'],
    ['betrag', 'Betrag'], ['kstSoll', 'KST Soll'], ['kstHaben', 'KST Haben'],
];

/** Upload a Rohdaten file, confirm the auto-detected mapping in a preview, then commit. */
export function UploadModal({ defaultPeriod, onClose, onCreated }: Props) {
    const [period, setPeriod] = useState(defaultPeriod);
    const [fileName, setFileName] = useState('');
    const [base64, setBase64] = useState('');
    const [sheetName, setSheetName] = useState<string | undefined>(undefined);
    const [password, setPassword] = useState('');
    const [needsPassword, setNeedsPassword] = useState(false);
    const [preview, setPreview] = useState<PreviewResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const runPreview = async (b64: string, name: string, sheet?: string, pw?: string) => {
        setBusy(true);
        setError(null);
        try {
            const mapping: ColumnMapping | undefined = sheet ? { sheetName: sheet } : undefined;
            const res = await lohnBuchungsbelegApi.preview(b64, name, /^\d{4}-\d{2}$/.test(period) ? period : '', mapping, pw || undefined);
            setPreview(res);
            setSheetName(res.parse.sheetName);
            setNeedsPassword(false);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Vorschau fehlgeschlagen';
            // Encrypted file: reveal the password field so the user can retry with a password.
            if (/passwortgesch|Passwort falsch/i.test(msg)) setNeedsPassword(true);
            setError(msg);
            setPreview(null);
        } finally {
            setBusy(false);
        }
    };

    const onFile = async (file: File | undefined) => {
        if (!file) return;
        setFileName(file.name);
        setPassword('');
        setNeedsPassword(false);
        const b64 = await fileToBase64(file);
        setBase64(b64);
        await runPreview(b64, file.name);
    };

    const changeSheet = async (sheet: string) => {
        setSheetName(sheet);
        if (base64) await runPreview(base64, fileName, sheet, password);
    };

    const canImport = !!preview && preview.parse.unmatchedFields.length === 0 && /^\d{4}-\d{2}$/.test(period) && !importing;

    const doImport = async () => {
        if (!canImport || !preview) return;
        setImporting(true);
        setError(null);
        try {
            const mapping: ColumnMapping | undefined = sheetName ? { sheetName } : undefined;
            const res = await lohnBuchungsbelegApi.createRun(base64, fileName, period, { mapping, password: password || undefined });
            onCreated(res.runId);
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Import fehlgeschlagen';
            // Finalized-period conflict → offer force
            if (/finalized/i.test(msg) && confirm(`${msg}\n\nMonat trotzdem überschreiben?`)) {
                try {
                    const res = await lohnBuchungsbelegApi.createRun(base64, fileName, period, { mapping: sheetName ? { sheetName } : undefined, force: true, password: password || undefined });
                    onCreated(res.runId);
                    return;
                } catch (e2) {
                    setError(e2 instanceof Error ? e2.message : 'Import fehlgeschlagen');
                }
            } else {
                setError(msg);
            }
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-slate-800">Neuen Monat importieren</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                </div>

                <div className="space-y-4 overflow-y-auto px-4 py-4 text-sm">
                    <div className="flex items-end gap-3">
                        <label className="block">
                            <span className="text-xs font-medium text-slate-600">Periode</span>
                            <input
                                type="month"
                                value={period}
                                onChange={e => setPeriod(e.target.value)}
                                className="mt-1 block rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-amber-400 focus:outline-none"
                            />
                        </label>
                        <label className="flex-1">
                            <span className="text-xs font-medium text-slate-600">Rohdaten-Datei (.xlsx / .xlsm)</span>
                            <div className="mt-1 flex items-center gap-2">
                                <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                                    <UploadCloud className="h-4 w-4" /> Datei wählen
                                    <input type="file" accept=".xlsx,.xlsm,.xls" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
                                </label>
                                {fileName && <span className="truncate text-xs text-slate-500">{fileName}</span>}
                            </div>
                        </label>
                    </div>

                    {/* Password — shown once a file is chosen; auto-highlighted when the file is encrypted. */}
                    {fileName && (
                        <label className="block">
                            <span className="text-xs font-medium text-slate-600">
                                Passwort {needsPassword ? <span className="text-rose-600">(Datei ist verschlüsselt)</span> : <span className="text-slate-400">(falls verschlüsselt)</span>}
                            </span>
                            <div className="mt-1 flex items-center gap-2">
                                <input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && base64) void runPreview(base64, fileName, sheetName, password); }}
                                    placeholder="Datei-Passwort"
                                    autoComplete="off"
                                    className={`block w-64 rounded border px-2 py-1.5 text-sm focus:outline-none ${needsPassword ? 'border-rose-300 focus:border-rose-400' : 'border-slate-300 focus:border-amber-400'}`}
                                />
                                {needsPassword && (
                                    <button
                                        onClick={() => base64 && runPreview(base64, fileName, sheetName, password)}
                                        disabled={busy || !password}
                                        className="rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                                    >
                                        Entsperren
                                    </button>
                                )}
                            </div>
                        </label>
                    )}

                    {busy && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Datei wird analysiert…</div>}
                    {error && <div className="rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">{error}</div>}

                    {preview && (
                        <div className="space-y-3">
                            {/* Sheet + header info */}
                            <div className="flex flex-wrap items-center gap-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                <div>
                                    <span className="font-medium">Sheet:</span>{' '}
                                    {preview.parse.sheets.length > 1 ? (
                                        <select value={sheetName} onChange={e => changeSheet(e.target.value)} className="rounded border border-slate-300 px-1 py-0.5 text-xs">
                                            {preview.parse.sheets.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    ) : <span>{preview.parse.sheetName}</span>}
                                </div>
                                <div><span className="font-medium">Header-Zeile:</span> {preview.parse.headerRow}</div>
                                <div><span className="font-medium">Datenzeilen:</span> {preview.parse.totalDataRows}</div>
                            </div>

                            {/* Column mapping */}
                            <div>
                                <div className="mb-1 text-xs font-medium text-slate-600">Erkannte Spalten-Zuordnung</div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                                    {REQUIRED_FIELDS.map(([field, label]) => {
                                        const mapped = preview.parse.mapping[field as keyof typeof preview.parse.mapping];
                                        return (
                                            <div key={field} className="flex items-center gap-1">
                                                {mapped ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />}
                                                <span className="text-slate-500">{label}:</span>
                                                <span className={mapped ? 'font-medium text-slate-700' : 'text-rose-600'}>{mapped ?? 'nicht gefunden'}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {preview.parse.unmatchedFields.length > 0 && (
                                    <div className="mt-2 flex items-start gap-1 rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        <span>Pflichtspalten fehlen: {preview.parse.unmatchedFields.join(', ')}. Bitte prüfe das Sheet / die Header-Zeile.</span>
                                    </div>
                                )}
                            </div>

                            {/* Computed preview summary */}
                            <div className="flex items-center gap-4 rounded border border-slate-200 px-3 py-2 text-xs">
                                <div>
                                    <span className="text-slate-500">Checksum: </span>
                                    <span className={`font-mono font-semibold ${preview.voucher.balanced ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {fmtMoney(preview.voucher.checksum, false)}
                                    </span>
                                    <span className="ml-1 text-slate-400">{preview.voucher.balanced ? '(ausgeglichen)' : '(Differenz)'}</span>
                                </div>
                                <div className="text-slate-400">·  {preview.voucher.lines.length} Buchungszeilen berechnet</div>
                                {(preview.voucher.unmatched?.length ?? 0) > 0 && (
                                    <div className="flex items-center gap-1 text-amber-600">
                                        <AlertTriangle className="h-3.5 w-3.5" />
                                        {preview.voucher.unmatched.length} nicht zugeordnete Lohnart(en)
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
                    <button onClick={onClose} className="rounded px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700">Abbrechen</button>
                    <button
                        onClick={doImport}
                        disabled={!canImport}
                        className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                        {importing && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Importieren
                    </button>
                </div>
            </div>
        </div>
    );
}
